/**
 * « Questions reliées » d'une SKILLCARD (spec § 12, règle validée : chaque
 * question d'exercice peut être reliée à une carte ; une bonne réponse lui
 * verse l'XP de la question, plafonnée au `xp_max`).
 *
 * Parcours : rechercher un exercice → cocher ses questions (principal /
 * secondaire). Seules les cartes PUBLIÉES créditent (garde serveur).
 */

import { useMemo, useState } from 'react';
import { ChevronDown, Link2, Search, Trash2 } from 'lucide-react';
import { cn, MathText } from '@jeraisonne/ui';
import { EXERCISE_SEARCH_MIN_LEN, useSearchExercises } from '@/lib/tutor/queries';
import {
  useAddCardQuestionLink,
  useCardQuestionLinks,
  useExerciseQuestions,
  useRemoveCardQuestionLink,
  type CardQuestionLink,
  type CardQuestionRole,
  type ExerciseQuestion,
} from '@/lib/cards/questions';

const inputClass =
  'border-ink-200 text-ink-900 focus:border-primary-400 min-h-[2.75rem] w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none';

function isLinked(links: CardQuestionLink[], q: ExerciseQuestion): CardQuestionLink | undefined {
  return links.find((l) => l.block_id === q.id);
}

export function CardQuestionsPanel({
  cardId,
  published,
  questionXp,
}: {
  cardId: string;
  published: boolean;
  /** XP gagnée par la carte à chaque bonne réponse (migration 0278). */
  questionXp?: number;
}) {
  const links = useCardQuestionLinks(cardId);
  const add = useAddCardQuestionLink(cardId);
  const remove = useRemoveCardQuestionLink(cardId);
  const [query, setQuery] = useState('');
  const [exerciseId, setExerciseId] = useState<string | null>(null);
  const [role, setRole] = useState<CardQuestionRole>('principal');
  const [error, setError] = useState<string | null>(null);
  // Replié par défaut : la liste peut être longue (dizaines de questions).
  const [open, setOpen] = useState(false);
  const search = useSearchExercises(query);
  const questions = useExerciseQuestions(exerciseId);

  const grouped = useMemo(() => {
    const map = new Map<string, { title: string; links: CardQuestionLink[] }>();
    for (const l of links.data ?? []) {
      const g = map.get(l.exercise_id) ?? { title: l.exercise_title, links: [] };
      g.links.push(l);
      map.set(l.exercise_id, g);
    }
    return [...map.entries()];
  }, [links.data]);

  const toggle = async (q: ExerciseQuestion) => {
    if (!exerciseId) return;
    setError(null);
    const existing = isLinked(links.data ?? [], q);
    try {
      if (existing) await remove.mutateAsync(existing.id);
      else await add.mutateAsync({ exerciseId, question: q, role });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action impossible.');
    }
  };

  return (
    <div className="border-ink-200 space-y-4 rounded-2xl border bg-white p-4">
      <h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={`card-questions-${cardId}`}
          onClick={() => setOpen((o) => !o)}
          className="text-ink-900 font-display -m-2 flex min-h-[2.75rem] w-[calc(100%+1rem)] items-center gap-2 rounded-xl p-2 text-left text-base font-bold active:scale-[0.99]"
        >
          <Link2 className="text-primary-500 h-5 w-5 shrink-0" aria-hidden />
          <span className="flex-1">Questions reliées</span>
          <span
            className="bg-primary-50 text-primary-700 rounded-full px-2.5 py-0.5 font-sans text-sm font-semibold tabular-nums"
            aria-label={`${links.data?.length ?? 0} question${(links.data?.length ?? 0) > 1 ? 's' : ''} reliée${(links.data?.length ?? 0) > 1 ? 's' : ''}`}
          >
            {links.isLoading ? '…' : (links.data?.length ?? 0)}
          </span>
          <ChevronDown
            aria-hidden
            className={cn(
              'text-ink-500 h-5 w-5 shrink-0 transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      </h2>

      {open && (
        <div id={`card-questions-${cardId}`} className="space-y-4">
          <p className="text-ink-500 text-sm">
            Chaque bonne réponse à une question reliée rapporte{' '}
            {questionXp ? `${questionXp} HORA` : 'ses HORA'} à la carte, jusqu’au maximum de HORA.
            {!published && ' Les élèves ne gagnent de l’HORA qu’une fois la carte publiée.'}
          </p>

          {grouped.length > 0 && (
            <ul className="space-y-3">
              {grouped.map(([exId, g]) => (
                <li key={exId} className="space-y-1">
                  <p className="text-ink-800 text-sm font-semibold">{g.title}</p>
                  <ul className="space-y-1">
                    {g.links.map((l, i) => (
                      <li key={l.id} className="flex items-center gap-2">
                        <span className="text-ink-600 flex-1 text-sm">
                          Question reliée {i + 1}
                          <span className="text-ink-400"> · {l.role}</span>
                        </span>
                        <button
                          type="button"
                          aria-label="Retirer le lien"
                          onClick={() => {
                            setError(null);
                            remove.mutate(l.id, {
                              onError: (e) =>
                                setError(e instanceof Error ? e.message : 'Action impossible.'),
                            });
                          }}
                          className="text-accent-600 hover:bg-accent-50 flex min-h-[2.75rem] min-w-[2.75rem] items-center justify-center rounded-lg"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}

          <div className="space-y-2">
            <label className="relative block">
              <span className="sr-only">Rechercher un exercice</span>
              <Search className="text-ink-400 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <input
                type="search"
                className={`${inputClass} pl-9`}
                placeholder="Rechercher un de tes exercices…"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setExerciseId(null);
                }}
              />
            </label>

            {!exerciseId && query.trim().length >= EXERCISE_SEARCH_MIN_LEN && (
              <div className="border-ink-100 max-h-64 overflow-y-auto rounded-lg border">
                {search.isLoading && <p className="text-ink-400 p-3 text-sm">Recherche…</p>}
                {search.data?.length === 0 && (
                  <p className="text-ink-400 p-3 text-sm">Aucun exercice.</p>
                )}
                {search.data?.map((ex) => (
                  <button
                    key={ex.id}
                    type="button"
                    onClick={() => setExerciseId(ex.id)}
                    className="hover:bg-ink-50 flex min-h-[2.75rem] w-full flex-col items-start px-3 py-2 text-left"
                  >
                    <span className="text-ink-800 text-sm font-medium">
                      <MathText>{ex.title}</MathText>
                    </span>
                    <span className="text-ink-400 text-xs">
                      {ex.chapter_title}
                      {ex.author && ` · ${ex.author.display_name}`}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {exerciseId && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-ink-600 text-xs font-semibold">Rôle de la carte :</span>
                  {(['principal', 'secondaire'] as const).map((r) => (
                    <button
                      key={r}
                      type="button"
                      aria-pressed={role === r}
                      onClick={() => setRole(r)}
                      className={cn(
                        'min-h-[2.75rem] rounded-full border px-3 text-sm',
                        role === r
                          ? 'border-primary-500 bg-primary-500 text-white'
                          : 'border-ink-200 text-ink-700 bg-white',
                      )}
                    >
                      {r === 'principal' ? 'Principale' : 'Secondaire'}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setExerciseId(null)}
                    className="text-primary-600 ml-auto min-h-[2.75rem] px-2 text-sm font-semibold"
                  >
                    Changer d’exercice
                  </button>
                </div>
                {questions.isLoading && (
                  <p className="text-ink-400 text-sm">Chargement des questions…</p>
                )}
                {questions.data?.length === 0 && (
                  <p className="text-ink-400 text-sm">Cet exercice n’a pas de question.</p>
                )}
                <ul className="space-y-1">
                  {questions.data?.map((q) => {
                    const linked = !!isLinked(links.data ?? [], q);
                    return (
                      <li key={q.id}>
                        <label className="hover:bg-ink-50 flex min-h-[2.75rem] cursor-pointer items-center gap-3 rounded-lg px-2">
                          <input
                            type="checkbox"
                            className="h-4 w-4"
                            checked={linked}
                            disabled={add.isPending || remove.isPending}
                            onChange={() => void toggle(q)}
                          />
                          <span className="text-ink-800 text-sm">
                            <span className="text-ink-400">Q{q.index + 1} · </span>
                            {q.label}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="text-accent-600 text-sm">
          {error}
        </p>
      )}
    </div>
  );
}
